import { Loader2 } from 'lucide-react';
import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePartnerProfile } from '../../hooks/usePartnerSettings';
import type { Partner } from '../../lib/api';
import { SETTINGS_STRINGS } from '../../lib/constants';

/**
 * Partner Profile Tab Component
 * Allows partners to manage their partner information and branding
 */
export const ProfileTab: React.FC = () => {
  const { partner, isLoading, updateProfile, isUpdating } = usePartnerProfile();
  const [formData, setFormData] = React.useState<Partial<Partner>>({});
  const [logoInputMethod, setLogoInputMethod] = React.useState<'upload' | 'url' | null>(null);
  const [showLogoOptions, setShowLogoOptions] = React.useState(false);

  React.useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name,
        domain: partner.domain,
        logoUrl: partner.logoUrl || '',
      });
    }
  }, [partner]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(formData);
  };

  const handleChange = (field: keyof Partner, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData(prev => ({ ...prev, logoUrl: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const strings = SETTINGS_STRINGS.ORGANIZATION_PROFILE;

  return (
    <div className="space-y-6">
      {/* Profile Form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Partner Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{strings.PARTNER_NAME_LABEL}</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={e => handleChange('name', e.target.value)}
                placeholder={strings.PARTNER_NAME_PLACEHOLDER}
                required
              />
            </div>

            {/* Domain & Logo */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="domain">{strings.DOMAIN_LABEL}</Label>
                <Input
                  id="domain"
                  value={formData.domain || ''}
                  onChange={e => handleChange('domain', e.target.value)}
                  placeholder={strings.DOMAIN_PLACEHOLDER}
                  required
                />
              </div>
            </div>

            {/* Logo Section */}
            <div className="space-y-3">
              <Label>{strings.LOGO_URL_LABEL}</Label>

              {/* Logo Preview */}
              {formData.logoUrl ? (
                <div className="space-y-2 flex flex-col items-start">
                  <div className="border border-border rounded-lg p-6 bg-muted/50">
                    <img
                      src={formData.logoUrl}
                      alt="Partner Logo"
                      className="h-20 object-contain"
                      onError={e => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  {!showLogoOptions ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLogoOptions(true)}>
                      Change logo
                    </Button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setLogoInputMethod('upload');
                          setShowLogoOptions(false);
                        }}>
                        Choose File
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setLogoInputMethod('url');
                          setShowLogoOptions(false);
                        }}>
                        Image URL
                      </Button>
                      <button
                        type="button"
                        onClick={() => setShowLogoOptions(false)}
                        className="text-sm text-muted-foreground hover:text-foreground">
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center space-y-2">
                    <p className="text-sm text-muted-foreground">No logo uploaded</p>
                    {!showLogoOptions ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowLogoOptions(true)}>
                        Add Logo
                      </Button>
                    ) : (
                      <div className="flex items-center justify-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setLogoInputMethod('upload');
                            setShowLogoOptions(false);
                          }}>
                          Choose File
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setLogoInputMethod('url');
                            setShowLogoOptions(false);
                          }}>
                          Image URL
                        </Button>
                        <button
                          type="button"
                          onClick={() => setShowLogoOptions(false)}
                          className="text-sm text-muted-foreground hover:text-foreground">
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Logo Input Methods */}
              {logoInputMethod === 'upload' && (
                <div className="border border-border rounded-lg p-4 space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Upload Image</p>
                    <button
                      type="button"
                      onClick={() => {
                        setLogoInputMethod(null);
                        setShowLogoOptions(false);
                      }}
                      className="text-xs text-primary hover:underline">
                      Cancel
                    </button>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      handleFileUpload(e);
                      setLogoInputMethod(null);
                      setShowLogoOptions(false);
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground">
                    Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP
                  </p>
                </div>
              )}

              {logoInputMethod === 'url' && (
                <div className="border border-border rounded-lg p-4 space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Image URL</p>
                    <button
                      type="button"
                      onClick={() => {
                        setLogoInputMethod(null);
                        setShowLogoOptions(false);
                      }}
                      className="text-xs text-primary hover:underline">
                      Cancel
                    </button>
                  </div>
                  <Input
                    type="url"
                    value={
                      formData.logoUrl && !formData.logoUrl.startsWith('data:') ? formData.logoUrl : ''
                    }
                    onChange={e => {
                      handleChange('logoUrl', e.target.value);
                      if (e.target.value) {
                        setLogoInputMethod(null);
                        setShowLogoOptions(false);
                      }
                    }}
                    placeholder={strings.LOGO_URL_PLACEHOLDER}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">Enter a public URL to an image file</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (partner) {
                    setFormData({
                      name: partner.name,
                      domain: partner.domain,
                      logoUrl: partner.logoUrl || '',
                    });
                  }
                }}
                disabled={isUpdating}>
                {strings.CANCEL_BUTTON}
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  strings.SAVE_BUTTON
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
